import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import ru from './i18n/translations/ru';
import en from './i18n/translations/en';
import tr from './i18n/translations/tr';
import gifts from './i18n/translations/gifts';

const translations = { ru: { ...ru, ...gifts.ru }, en: { ...en, ...gifts.en }, tr: { ...tr, ...gifts.tr } };
const SUPPORTED_CODES = ['ru', 'en', 'tr'];
const COOKIE_NAME = 'lang';
const DEFAULT_LANG = 'ru';

const I18nContext = createContext({
  lang: DEFAULT_LANG,
  setLang: () => {},
  t: (key) => key,
});

const FLAG_SVGS = {
  ru: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="18" viewBox="0 0 24 18"><rect width="24" height="6" fill="%23fff"/><rect y="6" width="24" height="6" fill="%23003f87"/><rect y="12" width="24" height="6" fill="%23d52b1e"/></svg>',
  en: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="18" viewBox="0 0 60 30"><clipPath id="s"><path d="M0,0 v30 h60 v-30 z"/></clipPath><clipPath id="t"><path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z"/></clipPath><g clip-path="url(%23s)"><path d="M0,0 v30 h60 v-30 z" fill="%23002063"/><path d="M0,0 L60,30 M60,0 L0,30" stroke="%23fff" stroke-width="6"/><path d="M0,0 L60,30 M60,0 L0,30" clip-path="url(%23t)" stroke="%23cf142b" stroke-width="4"/><path d="M30,0 v30 M0,15 h60" stroke="%23fff" stroke-width="10"/><path d="M30,0 v30 M0,15 h60" stroke="%23cf142b" stroke-width="6"/></g></svg>',
  tr: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="18" viewBox="0 0 24 18"><rect width="24" height="18" fill="%23e30a17"/><circle cx="9.5" cy="9" r="5" fill="%23fff"/><circle cx="10.6" cy="9" r="4" fill="%23e30a17"/><path d="M14.5 9l4-2.5-1.5 4.5-1.5-4.5z" fill="%23fff"/></svg>',
};

export const SUPPORTED_LANGS = [
  { code: 'ru', label: 'Русский', flag: '🇷🇺', icon: FLAG_SVGS.ru },
  { code: 'en', label: 'English', flag: '🇬🇧', icon: FLAG_SVGS.en },
  { code: 'tr', label: 'Türkçe', flag: '🇹🇷', icon: FLAG_SVGS.tr },
];

function getCookie(name) {
  if (typeof document === 'undefined') return '';
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : '';
}

function setCookie(name, value, days = 180) {
  if (typeof document === 'undefined') return;
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;
}

function normalizeLang(value) {
  if (!value || typeof value !== 'string') return DEFAULT_LANG;
  const lower = value.toLowerCase();
  return SUPPORTED_CODES.includes(lower) ? lower : DEFAULT_LANG;
}

function detectBrowserLang() {
  if (typeof navigator === 'undefined') return DEFAULT_LANG;
  const navLang = navigator.language || navigator.languages?.[0] || '';
  if (!navLang) return DEFAULT_LANG;
  if (navLang.toLowerCase().startsWith('tr')) return 'tr';
  if (navLang.toLowerCase().startsWith('en')) return 'en';
  return DEFAULT_LANG;
}

export function getStoredLang() {
  try {
    const fromCookie = getCookie(COOKIE_NAME);
    const fromStorage = typeof localStorage !== 'undefined' ? localStorage.getItem('lang') : '';
    const detected = detectBrowserLang();
    return normalizeLang(fromCookie || fromStorage || detected || DEFAULT_LANG);
  } catch {
    return DEFAULT_LANG;
  }
}

function persistLang(value) {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem('lang', value);
  }
  setCookie(COOKIE_NAME, value);
}

function translate(key, lang) {
  if (!key) return '';
  // Russian is the base language: if ru selected, return translation or key as-is.
  if (lang === 'ru') {
    const ruValue = translations.ru?.[key];
    return ruValue || key;
  }

  // For other languages: try current → ru (original text) → en.
  const direct = translations[lang]?.[key];
  if (direct) return direct;

  const ruValue = translations.ru?.[key];
  if (ruValue) return ruValue;

  const enValue = translations.en?.[key];
  if (enValue) return enValue;

  return key;
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => getStoredLang());

  const setLang = useCallback((next) => {
    const normalized = normalizeLang(next);
    setLangState(normalized);
    persistLang(normalized);
  }, []);

  const t = useCallback((key) => translate(key, lang), [lang]);

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}

export function getAcceptLanguageHeader() {
  const lang = getStoredLang();
  return `${lang},en;q=0.9,ru;q=0.8`;
}

